import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Theme,
  Typography,
  useMediaQuery,
  makeStyles,
  createStyles
} from '@material-ui/core';

import React from 'react';
import CloseIcon from '@material-ui/icons/Close';
import CustomStepper from './CustomStepper';
import MainButton from './MainButton';
import theme from './theme';

const useStyles = makeStyles(({ spacing, breakpoints }: Theme) =>
  createStyles({
    lineVertical: {
      height: '100%'
    },
    disabledStep: {
      color: 'grey'
    },
    padding: {
      paddingTop: spacing(0),
      paddingBottom: spacing(0),
      paddingRight: spacing(1)
    },
    noPadding: { display: 'flex', padding: spacing(0) },
    content: {
      overflowY: 'auto',
      paddingBottom: spacing(2),
      height: '95%'
    },
    background: {
      'div:first-child': {
        backgroundColor: 'rgba(0,0,0,0)'
      }
    },
    container: {
      [breakpoints.up('lg')]: {
        height: '75%'
      },
      [breakpoints.down('lg')]: {
        height: '90%'
      },
      [breakpoints.down('md')]: {
        height: '100%'
      }
    }
  })
);
export interface IFunctionalDialog extends DialogProps {
  open: boolean;
  onClose: () => void;
  steps: IStep[];
  isSubmitting?: boolean;
  handleSubmit: () => void;
  submitText?: string;
  maxWidth?: 'sm' | 'lg' | 'md';
  title: string;
  iconUri?: string;
  hideStepper?: boolean;
  hideActions?: boolean;
  fullScreen?: boolean;
}

export interface IStep {
  content: JSX.Element;
  label: string;
  validate: () => boolean | Promise<boolean>;
  subSteps?: IStep[];
}

const FunctionalDialog: React.FunctionComponent<IFunctionalDialog> = ({
  open,
  onClose,
  steps,
  isSubmitting,
  handleSubmit,
  submitText,
  maxWidth,
  title,
  iconUri,
  hideStepper,
  fullScreen,
  ...otherProps
}) => {
  const [activeStep, setActiveStep] = React.useState<number>(0);

  const classes = useStyles();

  const { validate, content, label } = steps[activeStep];

  React.useEffect(() => setActiveStep(0), [open]);

  const isSmallDevice = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth || 'md'}
      fullWidth
      PaperProps={{ className: classes.container }}
      fullScreen={isSmallDevice || fullScreen}
      {...otherProps}
    >
      <DialogTitle className={classes.padding} id="form-dialog-title">
        <Box display="flex" alignItems="center">
          {iconUri && <img src={iconUri} width="30px" alt="Dialog Icon" />}
          <Box mr={1} />
          <Typography variant="h6"> {title}</Typography>
          <Box flex={2} />
          <IconButton onClick={onClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: 0 }} dividers>
        <Box
          flexWrap="wrap"
          height="1"
          display="flex"
          alignItems="stretch"
          overflow="hidden"
        >
          {!hideStepper && (
            <React.Fragment>
              <Box
                maxHeight="1"
                flex={1}
                display={{ xs: 'none', md: 'flex' }}
                justifyContent="flex-start"
                style={{ overflowY: 'auto' }}
              >
                <CustomStepper
                  changeActiveStep={setActiveStep}
                  steps={steps}
                  activeStep={activeStep}
                />
              </Box>
              <Divider
                orientation="vertical"
                className={classes.lineVertical}
              />
            </React.Fragment>
          )}

          <Box flex={3} p={3} pt={3} className={classes.content}>
            {!hideStepper && (
              <React.Fragment>
                <Box
                  maxHeight="0.8"
                  display={{ xs: 'flex', md: 'none' }}
                  justifyContent="flex-start"
                  ml={-4}
                  mt={-3}
                >
                  <Stepper activeStep={activeStep}>
                    {steps.map((step, index) => (
                      <Step key={index}>
                        <StepLabel>{step.label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              </React.Fragment>
            )}

            <Typography variant="h6">{label}</Typography>
            <Box mb={4} />
            {content}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {activeStep !== 0 && (
          <MainButton
            text="Previous"
            disabled={isSubmitting}
            onClick={() => setActiveStep(activeStep - 1)}
            secondary
          />
        )}
        {activeStep !== steps.length - 1 ? (
          <MainButton
            text="Next"
            disabled={isSubmitting}
            onClick={async () =>
              (await validate()) && setActiveStep(activeStep + 1)
            }
          />
        ) : (
          <MainButton
            text={submitText || 'Submit'}
            disabled={isSubmitting}
            onClick={async () => {
              if (await validate()) {
                handleSubmit();
              }
            }}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FunctionalDialog;
